import { motion } from "framer-motion";

/**
 * Crisp SVG radial gauge — the shared chart language for all indicators.
 * value 0-100. tone: "score" (primary) or "confidence" (semantic by value).
 */
export function RadialGauge({ value = 0, label, sublabel, tone = "score", testId }) {
  const size = 168;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  const offset = c - (pct / 100) * c;

  let color = "hsl(var(--primary))";
  if (tone === "confidence") {
    color =
      pct >= 70
        ? "hsl(var(--success))"
        : pct >= 45
        ? "hsl(var(--warning))"
        : "hsl(var(--danger))";
  }

  return (
    <div
      className="flex flex-col items-center"
      data-testid={testId}
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      aria-label={`${label}: ${Math.round(pct)}${tone === "confidence" ? " percent" : " out of 100"}`}
    >
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="hsl(var(--secondary))"
            strokeWidth={stroke}
          />
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={c}
            initial={{ strokeDashoffset: c }}
            animate={{ strokeDashoffset: offset }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span
            className="font-mono text-4xl font-semibold tracking-tight tabular-nums"
            style={{ color }}
            data-testid={testId ? `${testId}-value` : undefined}
          >
            {Math.round(pct)}
            {tone === "confidence" && <span className="text-2xl">%</span>}
          </span>
          {sublabel && (
            <span className="mt-0.5 font-mono text-[11px] font-medium text-muted-foreground">
              {sublabel}
            </span>
          )}
        </div>
      </div>
      <span className="mt-3 text-xs font-semibold uppercase tracking-overline text-muted-foreground">
        {label}
      </span>
    </div>
  );
}
